// components/PickupCompletionRatio.tsx
import React, { useMemo } from "react";

type Props = {
    title?: string;
    periodLabel: string;
    completed?: number;
    planned?: number;
    loading?: boolean;
    error?: string | null;
    className?: string;
    height?: number; // px (default 280)
    colors?: {
        completed?: string;
        planned?: string;
        barBg?: string;
    };
};

const defaults = {
    completed: "#326e5a",
    planned: "#d1d5db",   // gray-300 (solo para la leyenda)
    barBg: "#f3f4f6",     // gray-100
};

const Dot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
    <span className="inline-flex items-center gap-2 text-xs text-gray-700">
    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
        {label}
  </span>
);

const PickupCompletionRatioComponent: React.FC<Props> = ({
                                                             title = "Completadas vs planificadas",
                                                             periodLabel,
                                                             completed = 0,
                                                             planned = 0,
                                                             loading = false,
                                                             error = null,
                                                             className = "",
                                                             height = 280,
                                                             colors = {},
                                                         }) => {
    const palette = { ...defaults, ...colors };

    const { pct, pctClamped, noData } = useMemo(() => {
        const c = Number.isFinite(completed) ? Number(completed) : 0;
        const p = Number.isFinite(planned) ? Number(planned) : 0;
        let pct = 0;
        if (c + p > 0) {
            pct = (c / (c + p)) * 100;
        }
        return {
            pct,
            pctClamped: Math.max(0, Math.min(pct, 100)),
            noData: c === 0 && p === 0,
        };
    }, [completed, planned]);

    return (
        <div className={`rounded-2xl border bg-white shadow-sm ${className}`} style={{ height }}>
            {/* Header */}
            <div className="p-4 flex items-center justify-between">
                <h3 className="font-semibold">{title}</h3>
                <div className="text-sm text-gray-600">{periodLabel}</div>
            </div>

            <div className="px-4 pb-6 relative h-[calc(100%-64px)]">
                {loading ? (
                    <div className="p-6 text-sm text-gray-500">Cargando datos…</div>
                ) : error ? (
                    <div className="p-6 text-sm text-rose-600">{error}</div>
                ) : (
                    <div className={`flex flex-col h-full ${noData ? "opacity-40 blur-[2px] pointer-events-none" : ""}`}>
                        {/* Porcentaje + chips */}
                        <div className="flex items-end justify-between">
                            <div className="text-4xl md:text-5xl font-semibold tracking-tight">
                                {planned > 0 || completed > 0 ? `${pct.toFixed(1)}%` : "—"}
                            </div>
                            <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 text-xs">
                  Completadas: <span className="font-medium ml-1">{completed}</span>
                </span>
                                <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 px-2.5 py-1 text-xs">
                  Planificadas: <span className="font-medium ml-1">{planned}</span>
                </span>
                            </div>
                        </div>

                        {/* ÚNICA barra: ocupa todo el ancho, sin segunda barra ni columnas */}
                        <div className="mt-4 grow flex items-center">
                            <div className="w-full h-3 rounded-full overflow-hidden" style={{ backgroundColor: palette.barBg }}>
                                <div
                                    className="h-full transition-[width] duration-500"
                                    style={{ width: `${pctClamped}%`, backgroundColor: palette.completed }}
                                    aria-label="Porcentaje completado"
                                />
                            </div>
                        </div>

                        {/* Leyenda */}
                        <div className="mt-3 flex items-center gap-4">
                            <Dot color={palette.completed} label="Completadas" />
                            <Dot color={palette.planned} label="Planificadas" />
                        </div>
                    </div>
                )}

                {/* Overlay sin datos */}
                {!(loading || error) && noData && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center">
                        <div className="rounded-xl bg-white/80 px-4 py-2 text-sm text-gray-700 shadow">
                            No hay datos para el periodo seleccionado.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const PickupCompletionRatio = React.memo(PickupCompletionRatioComponent);
